import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'shared/components';
import { AITextareaField } from './AITextareaField';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';
import { ProposalAIRichContent } from './ProposalAIRichContent';

interface Props {
  index: number;
  prompt: string;
  generated: GenerateTextSuggestionPayload | null;
  isGenerating: boolean;
  isGeneratedStale: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: React.MouseEventHandler<HTMLButtonElement>;
  onApply: React.MouseEventHandler<HTMLButtonElement>;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
}

export const TextSegmentAIPanel: React.FC<Props> = ({
  index,
  prompt,
  generated,
  isGenerating,
  isGeneratedStale,
  onPromptChange,
  onGenerate,
  onApply,
  onCancel,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="proposal-text-ai__panel">
      <div className="proposal-text-ai__header">
        <p className="t-label m-bottom--tny">
          {formatMessage({ id: 'proposal-ai.text.eyebrow' })}
        </p>
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.text.description' })}
        </p>
      </div>

      <AITextareaField
        id={`proposal-ai-text-prompt-${index}`}
        label={formatMessage({ id: 'proposal-ai.text.prompt.label' })}
        placeholder={formatMessage({
          id: 'proposal-ai.text.prompt.placeholder',
        })}
        value={prompt}
        onChange={onPromptChange}
        dataCy={`proposal-ai-text-prompt-${index}`}
        textareaClassName="proposal-text-ai__prompt"
      />

      <div className="proposal-ai-generator__action-row">
        <ProposalAIPrimaryButton
          label={
            isGenerating
              ? formatMessage({ id: 'proposal-ai.text.generate.loading' })
              : generated
                ? formatMessage({ id: 'proposal-ai.text.generate.regenerate' })
                : formatMessage({ id: 'proposal-ai.text.generate.action' })
          }
          onClick={onGenerate}
          disabled={isGenerating}
          btnSelector="proposal-ai-text-generate"
          size="med"
        />
        <Button
          style="ghost"
          size="med"
          onClick={onCancel}
          btnSelector="proposal-ai-text-cancel"
        >
          {formatMessage({ id: 'buttons.cancel' })}
        </Button>
      </div>

      {generated && (
        <>
          {isGeneratedStale && (
            <div className="proposal-ai-generator__notice proposal-ai-generator__notice--warning">
              <p className="t-text-2 m-bottom-0">
                {formatMessage({ id: 'proposal-ai.text.preview.stale' })}
              </p>
            </div>
          )}

          <div className="proposal-text-ai__preview">
            <p className="t-label m-bottom--tny">
              {formatMessage({ id: 'proposal-ai.text.preview.title' })}
            </p>
            <ProposalAIRichContent content={generated.description} />
          </div>

          <div className="proposal-ai-generator__action-row">
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.text.apply' })}
              onClick={onApply}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-text-apply"
              size="med"
            />
          </div>
        </>
      )}
    </div>
  );
};
